import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { useAddContractSearchParams } from '@/hooks/use-add-contract-search-params';

type AddContractContextValue = {
  isSheetOpen: boolean;
  channelId: number | undefined;
  channelName: string | undefined;
  setSheetOpen: (open: boolean) => void;
  setSelectedChannel: (id: number | undefined, name?: string) => void;
};

type AddContractProviderProps = PropsWithChildren<{
  /**
   * An account that cannot create contracts. The sheet renders in a portal that
   * no in-page overlay covers, so the state stays closed and the setters are
   * inert — otherwise the URL alone opens the form.
   */
  locked?: boolean;
}>;

const noop = () => {};

const AddContractContext = createContext<AddContractContextValue | undefined>(
  undefined,
);

export function AddContractProvider({
  locked = false,
  children,
}: AddContractProviderProps) {
  const {
    isSheetOpen,
    channelId,
    channelName,
    setSheetOpen,
    setSelectedChannel,
  } = useAddContractSearchParams();

  useEffect(() => {
    if (locked && isSheetOpen) {
      setSheetOpen(false);
    }
  }, [locked, isSheetOpen, setSheetOpen]);

  const value = useMemo(
    () => ({
      isSheetOpen: isSheetOpen && !locked,
      channelId: locked ? undefined : channelId,
      channelName: locked ? undefined : channelName,
      setSheetOpen: locked ? noop : setSheetOpen,
      setSelectedChannel: locked ? noop : setSelectedChannel,
    }),
    [
      locked,
      isSheetOpen,
      channelId,
      channelName,
      setSheetOpen,
      setSelectedChannel,
    ],
  );

  return (
    <AddContractContext.Provider value={value}>
      {children}
    </AddContractContext.Provider>
  );
}

export function useAddContractContext(): AddContractContextValue {
  const context = useContext(AddContractContext);
  if (context == null) {
    throw new Error(
      'useAddContractContext must be used within an AddContractProvider',
    );
  }
  return context;
}
